<template>
  <it-modal v-model="internalOpen" width="85%">
    <template v-slot:body>
      <div class="p-6 w-full">
        <div class="flex items-center mb-6">
          <span class="inline-flex hidden items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white mr-3">
            <Icon icon="tabler:rocket" class="w-6 h-6" />
          </span>
          <div>
            <h2 class="text-2xl font-bold">{{ t('deployModal.title', 'Deploy Agent') }}</h2>
            <div class="text-sm opacity-70">{{ t('deployModal.subtitle', 'Share your agent on the web or WhatsApp.') }}</div>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 grid-space-4">
          <div class="rounded-2xl p-5 border border-base-300 dark:border-gray-700 bg-gradient-to-br from-base-100 to-base-200">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center">
                <span class="inline-flex w-9 h-9 rounded-lg items-center justify-center bg-blue-100 text-blue-700 mr-3">
                  <Icon icon="heroicons:globe-alt" class="w-5 h-5" />
                </span>
                <h3 class="text-lg font-semibold">{{ t('deployModal.web.title', 'Web') }}</h3>
              </div>
              <span :class="['badge', webEnabled === true ? 'badge-success' : 'badge-ghost']">{{ webEnabled === true ? t('common.active', 'Active') : t('common.inactive','Inactive') }}</span>
            </div>

            <div class="space-y-4" :class="webEnabled === true ? '' : 'opacity-60 pointer-events-none'">
              <div>
                <div class="mb-2 text-sm font-medium">{{ t('deployModal.web.agentPage', 'Agent Page') }}</div>
                <div class="flex items-center space-x-2">
                  <input :value="agentPageUrl" readonly class="input input-bordered w-full" />
                  <button class="btn" @click="onCopy('url', agentPageUrl)">
                    <Icon v-if="copiedKind === 'url'" icon="tabler:check" class="w-5 h-5" />
                    <span v-else>{{ t('deployModal.copy', 'Copy') }}</span>
                  </button>
                </div>
              </div>

              <div>
                <div class="mb-2 text-sm font-medium">{{ t('deployModal.web.embedTitle', 'Embed on Website') }}</div>
                <div class="mb-2 text-xs opacity-70">{{ t('deployModal.web.embedDesc', 'Paste this snippet into your site and ensure the target selector exists.') }}</div>
                <div>
                  <div ref="monacoContainer" dir="ltr" class="w-full monaco-embed" style="min-height: 220px; border-radius: 10px; overflow: hidden; border: 1px solid var(--fallback-b3,oklch(var(--b3)/.2));"></div>
                  <div class="mt-2 flex justify-end">
                    <button class="btn btn-sm" @click="onCopy('snippet', embedSnippet)">
                      <Icon v-if="copiedKind === 'snippet'" icon="tabler:check" class="w-5 h-5" />
                      <span v-else>{{ t('deployModal.copy', 'Copy') }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <div class="alert alert-warning flex items-center justify-between">
                <div class="flex items-center">
                  <Icon icon="tabler:alert-triangle" class="w-5 h-5 mr-2" />
                  <span>{{ t('deployModal.web.securityNote', 'You must add your domain to the security list to allow embedding.') }}</span>
                </div>
                <button class="btn btn-warning btn-sm" @click="goToSecurity">{{ t('deployModal.web.goToSecurity','Open Security Settings') }}</button>
              </div>
            </div>
          </div>

          <div class="rounded-2xl p-5 border border-base-300 dark:border-gray-700 bg-gradient-to-br from-base-100 to-base-200">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center">
                <span class="inline-flex w-9 h-9 rounded-lg items-center justify-center bg-green-100 text-green-700 mr-3">
                  <Icon icon="tabler:brand-whatsapp" class="w-5 h-5" />
                </span>
                <h3 class="text-lg font-semibold">{{ t('deployModal.whatsapp.title', 'WhatsApp') }}</h3>
              </div>
              <span :class="['badge', whatsappEnabled === true ? 'badge-success' : 'badge-ghost']">{{ whatsappEnabled === true ? t('common.active', 'Active') : t('common.inactive','Inactive') }}</span>
            </div>

            <div class="space-y-4" :class="whatsappEnabled === true ? '' : 'opacity-60'">
              <div>
                <div class="mb-2 text-sm font-medium">{{ t('deployModal.whatsapp.numberLabel', 'WhatsApp Number') }}</div>
                <div class="flex items-center space-x-2">
                  <input :value="selectedWhatsappNumber" readonly class="input input-bordered w-full" :placeholder="t('deployModal.whatsapp.notConnected', 'No WhatsApp source bound')" />
                  <button class="btn" :disabled="selectedWhatsappNumber === '' || selectedWhatsappNumber.length === 0" @click="onCopy('wa', selectedWhatsappNumber)">
                    <Icon v-if="copiedKind === 'wa'" icon="tabler:check" class="w-5 h-5" />
                    <span v-else>{{ t('deployModal.copy', 'Copy') }}</span>
                  </button>
                </div>
              </div>
              <div class="grid grid-cols-1 text-xs opacity-70 grid-space-2">
                <div v-if="whatsappInfo.display !== ''">{{ t('deployModal.whatsapp.display', 'Display') }}: {{ whatsappInfo.display }}</div>
                <div v-if="whatsappInfo.type !== ''">{{ t('deployModal.whatsapp.type', 'Type') }}: {{ whatsappInfo.type }}</div>
                <div v-if="whatsappInfo.sourceId !== null">{{ t('deployModal.whatsapp.sourceId', 'Source ID') }}: {{ whatsappInfo.sourceId }}</div>
              </div>
            </div>
          </div>

          <div class="rounded-2xl p-5 border border-base-300 dark:border-gray-700 bg-gradient-to-br from-base-100 to-base-200">
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center">
                <span class="inline-flex w-9 h-9 rounded-lg items-center justify-center bg-purple-100 text-purple-700 mr-3">
                  <Icon icon="tabler:mail" class="w-5 h-5" />
                </span>
                <h3 class="text-lg font-semibold">{{ t('deployModal.email.title', 'Outlook (Email)') }}</h3>
              </div>
              <span :class="['badge', emailEnabled === true ? 'badge-success' : 'badge-ghost']">{{ emailEnabled === true ? t('common.active', 'Active') : t('common.inactive','Inactive') }}</span>
            </div>

            <div class="space-y-4" :class="emailEnabled === true ? '' : 'opacity-60'">
              <div>
                <div class="mb-2 text-sm font-medium">{{ t('deployModal.email.addressLabel', 'Email Address') }}</div>
                <div class="flex items-center space-x-2">
                  <input :value="emailAddress" readonly class="input input-bordered w-full" :placeholder="t('deployModal.email.notConnected', 'No email source bound')" />
                  <button class="btn" :disabled="emailAddress === '' || emailAddress.length === 0" @click="onCopy('email', emailAddress)">
                    <Icon v-if="copiedKind === 'email'" icon="tabler:check" class="w-5 h-5" />
                    <span v-else>{{ t('deployModal.copy', 'Copy') }}</span>
                  </button>
                </div>
              </div>
              <div class="grid grid-cols-1 text-xs opacity-70 grid-space-2">
                <div v-if="emailInfo.display !== ''">{{ t('deployModal.email.display', 'Display') }}: {{ emailInfo.display }}</div>
                <div v-if="emailInfo.sourceId !== null">{{ t('deployModal.email.sourceId', 'Source ID') }}: {{ emailInfo.sourceId }}</div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-end mt-8">
          <button class="btn" @click="closeModal">{{ t('deployModal.close', 'Close') }}</button>
        </div>
      </div>
    </template>
  </it-modal>
</template>

<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { useI18n } from '@/i18n';
import { Icon } from '@iconify/vue/dist/offline';
import loader from '@monaco-editor/loader';
import router from '@/router';
import { useToast } from 'vue-toastification';
import axios from '@/api/axiosInstance';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  agent: { type: Object, default: null },
  apiEndpoint: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const toast = useToast();

const internalOpen = ref(props.modelValue);
watch(() => props.modelValue, (val) => { internalOpen.value = val });
watch(internalOpen, (val) => { if (val === false) emit('update:modelValue', false) });

const webEnabled = ref(true);
const whatsappEnabled = ref(false);
const selectedWhatsappNumber = ref('');
const emailEnabled = ref(false);
const isLoadingStatuses = ref(false);
const emailAddress = ref('');
const whatsappInfo = ref({ display: '', type: '', sourceId: null });
const emailInfo = ref({ display: '', sourceId: null });

const agentPageUrl = computed(() => {
  if (props.agent === null || props.agent === undefined) return '';
  const routePath = props.agent.agenttype === 'PublicAgent' ? '/agent' : '/aiChatAgentAssistance';
  const origin = window.location.origin;
  return origin + routePath + '/' + props.agent.agentid;
});

const embedSnippet = computed(() => {
  if (props.agent === null || props.agent === undefined) return '';
  const src = 'https://7twenty-genai.azurewebsites.net/publicAgentSnippet/publicChatAgent.js';
  const agentId = props.agent.agentid;
  const endpoint = props.apiEndpoint;
  const target = '.chat-agent-v3-container';
  const openOnLoad = 'false';
  return '<script src="' + src + '" data-agent-id="' + agentId + '" data-api-endpoint="' + endpoint + '" data-target-element="' + target + '" data-open-on-load="' + openOnLoad + '" defer></' + 'script>';
});

const copiedKind = ref('');
function onCopy(kind, text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
    navigator.clipboard.writeText(text);
    copiedKind.value = kind;
    setTimeout(() => { if (copiedKind.value === kind) copiedKind.value = '' }, 1500);
  }
}

const monacoContainer = ref(null);
let monacoEditorInstance = null;
let isMonacoLoading = false;

async function initMonaco() {
  if (internalOpen.value === true) {
    if (monacoContainer.value !== null) {
      if (monacoEditorInstance === null) {
        if (isMonacoLoading === false) {
          isMonacoLoading = true;
          try {
            const monaco = await loader.init();
            monacoEditorInstance = monaco.editor.create(monacoContainer.value, {
              value: embedSnippet.value,
              language: 'html',
              theme: 'vs-dark',
              readOnly: true,
              automaticLayout: true,
              minimap: { enabled: false },
              fontSize: 14,
              wordWrap: 'on'
            });
            monacoEditorInstance.layout();
          } finally {
            isMonacoLoading = false;
          }
        }
      }
    }
  }
}

watch(embedSnippet, (val) => {
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.setValue(val);
  }
});

watch(internalOpen, async (val) => {
  if (val === true) {
    await nextTick();
    await initMonaco();
  } else {
    if (monacoEditorInstance !== null) {
      monacoEditorInstance.dispose();
      monacoEditorInstance = null;
    }
  }
});

onBeforeUnmount(() => {
  if (monacoEditorInstance !== null) {
    monacoEditorInstance.dispose();
    monacoEditorInstance = null;
  }
});

function closeModal() {
  internalOpen.value = false;
}

function goToSecurity() {
  router.push('/security');
}

function onToggleWeb() {
  
}

function onToggleWhatsapp() {
  
}

async function loadChannelStatuses() {
  if (props.agent === null || props.agent === undefined) return;
  isLoadingStatuses.value = true;
  try {
    const res = await axios.get(`/agents/${props.agent.agentid}/activeChannels`);
    const data = res.data;
    if (data !== undefined && data !== null) {
      webEnabled.value = data.web !== undefined && data.web !== null && data.web.active === true;
      whatsappEnabled.value = data.whatsapp !== undefined && data.whatsapp !== null && data.whatsapp.active === true;
      emailEnabled.value = data.email !== undefined && data.email !== null && data.email.active === true;
      if (typeof data.whatsapp?.number === 'string') {
        selectedWhatsappNumber.value = data.whatsapp.number;
      }
      if (typeof data.email?.address === 'string') {
        emailAddress.value = data.email.address;
      }
      if (data.whatsapp !== undefined && data.whatsapp !== null) {
        whatsappInfo.value = {
          display: typeof data.whatsapp.display === 'string' ? data.whatsapp.display : '',
          type: typeof data.whatsapp.type === 'string' ? data.whatsapp.type : '',
          sourceId: data.whatsapp.sourceId !== undefined && data.whatsapp.sourceId !== null ? data.whatsapp.sourceId : null
        };
      } else {
        whatsappInfo.value = { display: '', type: '', sourceId: null };
      }
      if (data.email !== undefined && data.email !== null) {
        emailInfo.value = {
          display: typeof data.email.display === 'string' ? data.email.display : '',
          sourceId: data.email.sourceId !== undefined && data.email.sourceId !== null ? data.email.sourceId : null
        };
      } else {
        emailInfo.value = { display: '', sourceId: null };
      }
    }
  } catch (e) {

  } finally {
    isLoadingStatuses.value = false;
  }
}

watch(internalOpen, async (val) => {
  if (val === true) {
    await loadChannelStatuses();
  }
})
</script>

<style scoped>
.pointer-events-none {
  pointer-events: none;
}
.monaco-embed {
  direction: ltr !important;
  unicode-bidi: embed !important;
}
.monaco-embed * {
  direction: ltr !important;
  unicode-bidi: embed !important;
}
</style>



